/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    ChangeDetectionStrategy,
    Component,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as StateNames from 'state-names';
import * as globalL10n from 'global-l10n';
import { StateAccessService } from 'ng/modules/core/services/state-access/state-access.service';
import { StateManager } from 'ajs/modules/core/services/state-manager/state-manager.service';
import { INavState } from '../../navigation.types';
import { NavigationService } from '../../services/navigation.service';
import * as l10n from './administration-vertical-nav.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Vertical Navigation component for the Administration pages.
 * @author alextsg
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'administration-vertical-nav',
    template: '<vertical-nav [states]="administrationStates"></vertical-nav>',
})
export class AdministrationVerticalNavComponent {
    /**
     * List of navigation states and children that should be shown in the nav.
     */
    public administrationStates: INavState[] = [];

    constructor(
        navigationService: NavigationService,
        private readonly stateAccessService: StateAccessService,
        private readonly stateManagerService: StateManager,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const labelsHash = {
            [StateNames.ADMINISTRATION_USERS]: {
                icon: 'employee',
                label: l10nService.getMessage(l10nKeys.accountsLabel),
            },
            [StateNames.USERS_USER_LIST]: {
                label: l10nService.getMessage(l10nKeys.usersLabel),
            },
            [StateNames.USERS_USER_PROFILE_LIST]: {
                label: l10nService.getMessage(l10nKeys.userProfilesLabel),
            },
            [StateNames.USERS_ROLE_LIST]: {
                label: l10nService.getMessage(globalL10nKeys.rolesLabel),
            },
            [StateNames.USERS_TENANT_LIST]: {
                label: l10nService.getMessage(l10nKeys.tenantsLabel),
            },
            [StateNames.USERS_TENANT_SETTINGS]: {
                label: l10nService.getMessage(globalL10nKeys.tenantSettingsLabel),
            },
            [StateNames.ADMINISTRATION_SYSTEM_SETTINGS]: {
                icon: 'cog',
                label: l10nService.getMessage(l10nKeys.systemSettingsLabel),
            },
            [StateNames.ADMINISTRATION_EXTERNAL_SERVICES]: {
                icon: 'internet-of-things',
                label: l10nService.getMessage(l10nKeys.externalServicesLabel),
            },
            [StateNames.EXTERNAL_CLOUD_SERVICES]: {
                label: l10nService.getMessage(l10nKeys.cloudServicesLabel),
            },
            [StateNames.EXTERNAL_CUSTOMER_PROGRAM]: {
                label: l10nService.getMessage(globalL10nKeys.customerProgramLabel),
            },
            [StateNames.ADMINISTRATION_LICENSING]: {
                icon: 'certificate',
                label: l10nService.getMessage(l10nKeys.licensingLabel),
            },
            [StateNames.ADMINISTRATION_CONTROLLER_DETAIL]: {
                icon: 'atom',
                label: l10nService.getMessage(globalL10nKeys.controllerLabel),
            },
            [StateNames.CONTROLLER_DETAIL_CLUSTER]: {
                label: l10nService.getMessage(l10nKeys.nodesLabel),
            },
            [StateNames.CONTROLLER_DETAIL_SOFTWARE]: {
                label: l10nService.getMessage(l10nKeys.softwareLabel),
            },
            [StateNames.CONTROLLER_DETAIL_UPDATE]: {
                label: l10nService.getMessage(l10nKeys.updateLabel),
            },
            [StateNames.CONTROLLER_DETAIL_UPLOAD_HSM_PACKAGES]: {
                label: l10nService.getMessage(l10nKeys.uploadHsmPackagesLabel),
            },
            [StateNames.CONTROLLER_DETAIL_BACKUP]: {
                label: l10nService.getMessage(l10nKeys.configurationBackupLabel),
            },
            [StateNames.CONTROLLER_DETAIL_REPORTS]: {
                label: l10nService.getMessage(l10nKeys.reportsLabel),
            },
            [StateNames.ADMINISTRATION_USER_CREDENTIALS]: {
                icon: 'id-badge',
                label: l10nService.getMessage(l10nKeys.userCredentialsLabel),
            },
            [StateNames.ADMINISTRATION_SUPPORT]: {
                icon: 'help',
                label: l10nService.getMessage(l10nKeys.supportLabel),
            },
            [StateNames.SUPPORT_TECH_SUPPORT]: {
                label: l10nService.getMessage(l10nKeys.techSupportLabel),
            },
            [StateNames.SUPPORT_CRASH_REPORTS]: {
                label: l10nService.getMessage(l10nKeys.crashReportsLabel),
            },
            [StateNames.ADMINISTRATION_DASHBOARD]: {
                icon: 'dashboard',
                label: l10nService.getMessage(globalL10nKeys.dashboardLabel),
            },
        };

        const states = navigationService.getChildren(StateNames.ADMINISTRATION)
            .map(state => {
                const { label, icon } = labelsHash[state.name];

                return {
                    ...state,
                    label,
                    icon,
                    children: state.children?.map(childState => {
                        const childStateProps = labelsHash[childState.name];

                        return {
                            ...childState,
                            label: childStateProps?.label,
                            icon: childStateProps?.icon,
                        };
                    }),
                };
            });

        this.administrationStates = states.reduce((acc, state) => {
            if (state.name === StateNames.ADMINISTRATION_USERS) {
                if (!stateManagerService.isNavigable(StateNames.ADMINISTRATION_USERS)) {
                    return acc;
                }

                return [
                    ...acc,
                    {
                        ...state,
                        children: state.children.filter(childState => {
                            return childState.name === StateNames.USERS_TENANT_SETTINGS ?
                                this.stateAccessService.isAccessible(
                                    StateNames.USERS_TENANT_SETTINGS,
                                ) :
                                true;
                        }),
                    },
                ];
            }

            return [
                ...acc,
                state,
            ];
        }, []);
    }
}
